import { useQueryClient } from "@tanstack/vue-query";

import { queries } from "~/queries";
import { useRefetchEventWithProductsInfiniteQuery } from "~/queries/catalog/events";
import { CategoryTabs } from "~/types/Category";

type BidData = {
  productId: string;
  productSlug: string;
  eventSlug?: string;
  eventId?: string;
  categoryId?: string;
  updateMaxBid?: boolean;
};

const useUpdatePlatformAfterBid = () => {
  const queryClient = useQueryClient();
  const refetchEventWithProducts = useRefetchEventWithProductsInfiniteQuery();

  const { userId } = useUser();

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  return async ({
    productSlug,
    productId,
    eventSlug,
    eventId,
    categoryId,
    updateMaxBid,
  }: BidData) => {
    const queriesToInvalidate: Promise<void>[] = [];

    // product queries
    queriesToInvalidate.push(
      queryClient.invalidateQueries({
        queryKey: queries.products.details(productSlug, platformId).queryKey,
      }),
    );

    queriesToInvalidate.push(
      queryClient.invalidateQueries({
        queryKey: queries.products.details(productId, platformId).queryKey,
      }),
    );

    queriesToInvalidate.push(
      queryClient.invalidateQueries({
        queryKey: queries.products.bids(productId, platformId).queryKey,
      }),
    );

    // user bid queries
    queriesToInvalidate.push(
      queryClient.invalidateQueries({
        queryKey: ["me", "bids", "infiniteList"],
      }),
    );

    if (userId.value) {
      queriesToInvalidate.push(
        queryClient.invalidateQueries({
          queryKey: queries.bids.forCurrentUser(userId.value, platformId)
            .queryKey,
          exact: true,
        }),
      );
    }

    if (categoryId) {
      queriesToInvalidate.push(
        queryClient.invalidateQueries({
          queryKey: [
            "events",
            "overview",
            "infiniteList",
            {
              // Only update the live tab. The other tabs don't display new bids coming in
              liveStatus: CategoryTabs.Live,
              categoryId: categoryId,
            },
          ],
        }),
      );
    }

    // event queries
    if (eventSlug) {
      queriesToInvalidate.push(
        queryClient.invalidateQueries({
          queryKey: [
            "products",
            "infiniteList",
            { eventIdentifier: eventSlug },
          ],
        }),
      );

      queriesToInvalidate.push(
        queryClient.invalidateQueries({
          queryKey: queries.events.details(eventSlug, platformId).queryKey,
        }),
      );
    }

    if (eventId) {
      refetchEventWithProducts(eventId);
    }

    if (updateMaxBid) {
      queriesToInvalidate.push(
        queryClient.invalidateQueries({
          queryKey: queries.bids.max(productId, platformId).queryKey,
        }),
      );
    }

    await Promise.all(queriesToInvalidate);
  };
};

export default useUpdatePlatformAfterBid;
